// ============================================
// Danh sách múi giờ IANA cho các ô chọn timezone của store-admin.
//
// Trước đây múi giờ giờ cấm rượu là ô NHẬP TAY: sai một ký tự
// (`Europe/Warszawa`, `europe/warsaw`, `GMT+2`) là giờ cấm tính lệch mà không
// ai biết — BE nhận nguyên chuỗi và chỉ vỡ lúc chạy. File này dựng sẵn danh
// sách hợp lệ để form chỉ cần cho CHỌN.
//
// Hệ thống vận hành ở Ba Lan / Ukraine nên chỉ lấy vùng `Europe/*`: đổ cả ~400
// múi giờ toàn cầu vào <select> thì không ai đọc nổi.
// ============================================

/** Hai thị trường chính — luôn nằm đầu danh sách, đúng thứ tự này. */
const PINNED_TIMEZONES = ['Europe/Warsaw', 'Europe/Kyiv'] as const;

/**
 * Danh sách rút gọn dùng khi `Intl.supportedValuesOf` không có (trình duyệt cũ).
 * Không cần đủ, chỉ cần đủ để người dùng vẫn chọn được múi giờ của mình.
 */
const FALLBACK_TIMEZONES = [
  'Europe/Warsaw',
  'Europe/Kyiv',
  'Europe/Berlin',
  'Europe/Prague',
  'Europe/Bratislava',
  'Europe/Vilnius',
  'Europe/Budapest',
  'Europe/Bucharest',
  'Europe/London',
  'Europe/Chisinau',
];

export interface TimezoneOption {
  /** Chuỗi IANA gửi lên BE, ví dụ `Europe/Warsaw`. */
  value: string;
  /** Nhãn hiển thị, ví dụ `Warsaw (Europe/Warsaw, UTC+02:00)`. */
  label: string;
}

/**
 * Lấy toàn bộ múi giờ `Europe/*` mà trình duyệt biết.
 *
 * `Intl.supportedValuesOf` là API mới — có thể KHÔNG tồn tại, và một vài bản
 * cài đặt còn ném lỗi với key không hỗ trợ. Cả hai trường hợp đều rơi về
 * `FALLBACK_TIMEZONES` chứ không được để select rỗng.
 */
function listEuropeanTimeZones(): string[] {
  try {
    const supportedValuesOf = (Intl as unknown as {
      supportedValuesOf?: (key: string) => string[];
    }).supportedValuesOf;

    if (typeof supportedValuesOf !== 'function') return [...FALLBACK_TIMEZONES];

    const all = supportedValuesOf('timeZone');
    if (!Array.isArray(all) || all.length === 0) return [...FALLBACK_TIMEZONES];

    const european = all.filter((tz) => typeof tz === 'string' && tz.startsWith('Europe/'));
    return european.length > 0 ? european : [...FALLBACK_TIMEZONES];
  } catch {
    return [...FALLBACK_TIMEZONES];
  }
}

/**
 * Độ lệch UTC HIỆN TẠI của một múi giờ, dạng `UTC+02:00`.
 *
 * Luôn hỏi `Intl.DateTimeFormat`, TUYỆT ĐỐI không tự cộng trừ bằng tay: quy tắc
 * giờ mùa hè khác nhau theo từng vùng và từng năm, tính tay chắc chắn sai.
 *
 * Trả `null` khi trình duyệt không hỗ trợ `timeZoneName: 'longOffset' |
 * 'shortOffset'` (ném RangeError) — lúc đó nhãn chỉ hiện tên, vẫn dùng được.
 */
function getUtcOffsetLabel(timeZone: string): string | null {
  for (const timeZoneName of ['longOffset', 'shortOffset'] as const) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName,
      }).formatToParts(new Date());
      const raw = parts.find((part) => part.type === 'timeZoneName')?.value;
      if (raw) return normalizeOffset(raw);
    } catch {
      // Thử tiếp lựa chọn sau; hết thì trả null ở dưới.
    }
  }
  return null;
}

/** `GMT+2` / `GMT+02:00` / `GMT` → `UTC+02:00` / `UTC+00:00`. */
function normalizeOffset(raw: string): string {
  if (raw === 'GMT' || raw === 'UTC') return 'UTC+00:00';

  const match = /^(?:GMT|UTC)([+-])(\d{1,2})(?::?(\d{2}))?$/.exec(raw);
  if (!match) return raw.replace('GMT', 'UTC');

  const [, sign, hours, minutes] = match;
  return `UTC${sign}${hours.padStart(2, '0')}:${minutes ?? '00'}`;
}

/** `Europe/Warsaw` → `Warsaw`; `America/Argentina/Buenos_Aires` → `Buenos Aires`. */
function getCityName(timeZone: string): string {
  const segments = timeZone.split('/');
  return (segments[segments.length - 1] || timeZone).replace(/_/g, ' ');
}

/**
 * Nhãn dễ đọc cho một múi giờ: `Warsaw (Europe/Warsaw, UTC+02:00)`.
 * Dùng được cho cả chuỗi lạ ngoài danh sách (khi đó thường không có offset).
 */
export function formatTimezoneLabel(timeZone: string): string {
  const city = getCityName(timeZone);
  const offset = getUtcOffsetLabel(timeZone);

  // Chuỗi không có vùng (`UTC`, hoặc rác kiểu `GMT+2` do thời nhập tay): lặp
  // lại chính nó thành "GMT+2 (GMT+2)" chỉ tổ khó đọc.
  if (city === timeZone) return offset ? `${timeZone} (${offset})` : timeZone;

  return offset ? `${city} (${timeZone}, ${offset})` : `${city} (${timeZone})`;
}

function buildTimezoneOptions(): TimezoneOption[] {
  const all = listEuropeanTimeZones();
  const unique = Array.from(new Set(all));

  const pinned = PINNED_TIMEZONES.filter((tz) => unique.includes(tz));
  const rest = unique
    .filter((tz) => !pinned.includes(tz as (typeof PINNED_TIMEZONES)[number]))
    .sort((a, b) => getCityName(a).localeCompare(getCityName(b), 'en'));

  return [...pinned, ...rest].map((tz) => ({ value: tz, label: formatTimezoneLabel(tz) }));
}

/**
 * Danh sách múi giờ cho <select>, tính MỘT LẦN ở module scope.
 *
 * Dựng lại mỗi lần render là gọi `Intl.DateTimeFormat` vài trăm lần cho mỗi
 * phím gõ trong form — đủ để modal giật thấy rõ.
 */
export const EUROPEAN_TIMEZONE_OPTIONS: TimezoneOption[] = buildTimezoneOptions();

/** Giá trị này có nằm trong danh sách dựng sẵn không? */
export function isKnownTimezone(value: string): boolean {
  return EUROPEAN_TIMEZONE_OPTIONS.some((option) => option.value === value);
}

/**
 * Option PHẢI CHÈN THÊM để `<select>` hiển thị được đúng giá trị server đang lưu.
 *
 * Store cũ có thể đang giữ một chuỗi ngoài `Europe/*` (`Asia/Ho_Chi_Minh`) hoặc
 * rác từ thời còn nhập tay (`GMT+2`, `europe/warsaw`). `<select>` chỉ khớp
 * option có `value` TRÙNG Y HỆT: không có option cho chính giá trị đó thì
 * trình duyệt tự nhảy về option đầu tiên, và người dùng chỉ cần MỞ form rồi bấm
 * Lưu là múi giờ bị đổi âm thầm — đúng loại lỗi mà giờ cấm bán rượu không được
 * phép dính.
 *
 * So khớp bằng chuỗi THÔ (không trim) vì lý do trên; chỉ NHÃN mới trim cho dễ
 * đọc. Trả `null` khi giá trị rỗng hoặc đã có sẵn trong danh sách.
 *
 * Dùng CHUNG cho cả ô múi giờ cấp cửa hàng lẫn ô múi giờ của giờ cấm — hai chỗ
 * này phải hành xử giống hệt nhau, chép đôi logic là sớm muộn cũng lệch.
 */
export function getCustomTimezoneOption(value: string): TimezoneOption | null {
  if (!value.trim() || isKnownTimezone(value)) return null;
  return { value, label: formatTimezoneLabel(value.trim()) };
}
